import {
    useState,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    CSSProperties,
} from "react"
import momentTz from "moment-timezone"
import MainContentContainer from "components/shareable/MainContentContainer"
import isEqual from "lodash/isEqual"
import { BackgroundTypes, SavingDraftStatus } from "modules/Types"
import { PreSaveDataContext } from "../Data/PreSaveProvider"
import PostCreation from "./Components/PostCreation/PostCreation"
import { track } from "analytics"
import { useHistory } from "react-router-dom"
import { useLocation } from "react-router-dom"
import Grid from "@material-ui/core/Grid"
import TopBar from 'components/shareable/TopBar/TopBar'
import some from "lodash/some"
import isEmpty from "lodash/isEmpty"
import {
    getMainTitle,
    getContainerClassName,
    getRecordPreviewContainerStyles,
    checkIsValidName,
} from "./utils"
import {
    ARTWORK,
    STACKED,
} from "./constants"
import { scrollIntoView } from "helpers/StyleUtils"
import useStyles from "pages/MarketingPage/PreSave/styles"
import RecordPreview from "components/shareable/RecordPreview"
import { useDraftDataGetter, useDraftDataSetter } from "./Hooks/DraftsHandlers"
import useScreen from "Hooks/useScreen"
import useCurrentBrand from 'Hooks/useCurrentBrand'
import Colors from "modules/Colors"
import CustomizeTab from "./Components/CustomizeTab"
import DetailsTab from "./Components/DetailsTab/DetailsTab"
import ReviewTab from "./Components/ReviewTab"
import { clsx } from "modules/Utils"
import Loader from "elements/Loader"
import ModalSymphony from "elements/ModalSymphony"
import TabsSymphony, { TabItem } from "elements/TabsSymphony"
import TabSymphony from "elements/TabSymphony"
import PrevNextFooter from "elements/PrevNextFooter"
import { PlaybookType } from "modules/Const"
import ButtonSymphony from "elements/ButtonSymphony"
import PageSymphony from "elements/PageSymphony"

export enum PresaveStep {
    DETAILS = "DETAILS",
    CUSTOMIZE = "CUSTOMIZE",
    REVIEW = "REVIEW",
}

const PRESAVE_TABS = [
    {
        id: PresaveStep.CUSTOMIZE,
        title: "Customize",
    },
    {
        id: PresaveStep.DETAILS,
        title: "Details",
    },
    {
        id: PresaveStep.REVIEW,
        title: "Review",
    },
]

interface PreSaveProps {
    setGoal: Dispatch<SetStateAction<string | null>>
}

// NOTE: component()
const PreSave = ({
    setGoal,
}: PreSaveProps) => {
    const location = useLocation()
    const history = useHistory()
    const classes = useStyles()
    const { mobileView } = useScreen()
    const [, path] = location.pathname.split("/")
    const draft = new URLSearchParams(location.search).get("draft")
    const { currentBrand } = useCurrentBrand()
    const {
        loading,
        showPostCreation,
        customize,
        details,
        postCreationErrorType,
        dataComplete,
        setPostCreationErrorType,
        setShowPostCreation,
        saveCustomize,
        saveDetails,
    } = useContext(PreSaveDataContext)

    const {
        titleAlignment,
        subtitle,
        primaryColor,
        buttonColor,
        styleType,
    } = customize

    const {
        musicPlatforms,
    } = details

    const { releaseDate, releaseUrlValid: isValidReleaseUrl, releaseUrl } = details
    const { website, name: contentName, image: brandArtwork } = currentBrand || {}
    const brandId = currentBrand?.id || 0
    const [showPreview, setShowPreview] = useState<boolean>(false)
    const [selectedPreSaveTab, setSelectedPreSaveTab] = useState<TabItem>(PRESAVE_TABS[0])
    const [isSavingDraft, setIsSavingDraft] = useState(false)
    const [retrievingDraftData, setRetrievingDraftData] = useState(!!draft)
    const [requiredDataChanged, setRequiredDataChanged] = useState(false)
    const [campaignDraftData, setCampaignDraftData] = useState<Record<
        string,
        unknown
    > | null>(null)
    const [savingDraftStatus, setSavingDraftStatus] = useState(SavingDraftStatus.INITIAL_SAVING_STATE);

    const mainTitle = getMainTitle({
        showPostCreation,
        loading,
        errorType: postCreationErrorType,
    })
    const containerClassName = getContainerClassName(showPostCreation, mobileView)
    const steps = [
        { description: "Customize", name: PresaveStep.CUSTOMIZE },
        { description: "Details", name: PresaveStep.DETAILS },
        { description: "Review", name: PresaveStep.REVIEW },
    ]
    const isReviewStepDisabled = some([
        (!isEmpty(releaseUrl) && !isValidReleaseUrl),
        customize.releaseTitle && !checkIsValidName(customize.releaseTitle),
        !releaseDate || releaseDate === "Invalid date"
    ])
    const selectedBackgroundType =
        customize?.backgroundType || website?.bgType || ARTWORK
    const backgroundColor =
        customize?.backgroundColor || website?.bgColor || Colors.DARK_GRAY
    const {
        style: previewModalContainerStyle,
        className: previewModalContainerClassName,
    } = getRecordPreviewContainerStyles({
        classes,
        backgroundColor,
        backgroundType: selectedBackgroundType,
    })
    const showLoading = !!draft && retrievingDraftData

    const isNextButtonDisabled = () => {
        if (selectedPreSaveTab.id === PresaveStep.CUSTOMIZE) {
            return !customize?.releaseTitle || !checkIsValidName(customize.releaseTitle)
        }

        if (selectedPreSaveTab.id === PresaveStep.DETAILS) {
            return isReviewStepDisabled
        }

        return false
    }

    const handleClickCloseTopBar = () => {
        if (showPostCreation) {
            setPostCreationErrorType(null)
            setGoal(null)
        } else {
            momentTz.tz.setDefault()
            if (isEqual(path, "fans")) setGoal(null)
            else history.push("/marketing")
        }
    }

    const handleClickBackButton = () => {
        if (selectedPreSaveTab.id === PresaveStep.REVIEW) {
            setSelectedPreSaveTab(PRESAVE_TABS[1])
        } else if (selectedPreSaveTab.id === PresaveStep.DETAILS) {
            setSelectedPreSaveTab(PRESAVE_TABS[0])
        } else if (selectedPreSaveTab.id === PresaveStep.CUSTOMIZE) {
            momentTz.tz.setDefault()
            if (isEqual(path, "fans")) setGoal(null)
            else history.push("/marketing")
        }
    }

    const handleClickNextButton = () => {
        const commonTrackData = {
            playbookType: PlaybookType.META_PRESAVES,
            brand_id: currentBrand?.id,
            brand_name: currentBrand?.name,
            brand_slug: currentBrand?.slug,
            ...(customize ? customize : {}),
            ...(details ? details : {}),
        }

        if (selectedPreSaveTab.id === PresaveStep.CUSTOMIZE) {
            setSelectedPreSaveTab(PRESAVE_TABS[1])
            track("Customize Completed", {
                ...commonTrackData,
            })
        } else if (selectedPreSaveTab.id === PresaveStep.DETAILS) {
            setSelectedPreSaveTab(PRESAVE_TABS[2])
            track("Details Completed", {
                ...commonTrackData,
            })
        } else {
            // NOTE CONNER: this is when you click the Finish button
            // showPostCreation mounts the PostCreation.tsx component
            // PostCreation.tsx useEffect([]) runs the createCampaign() function
            cancelSavingDraft()
            setShowPostCreation(true)
        }
    }

    useEffect(() => {
        scrollIntoView()
    }, [selectedPreSaveTab])

    useDraftDataGetter({
        draft,
        website,
        setters: {
            setSelectedTab: setSelectedPreSaveTab,
            setRetrievingDraftData,
            setCampaignDraftData,
            setIsSavingDraft,
            saveCustomize,
            saveDetails
        } as Record<string, Dispatch<unknown>>,
    })

    const { cancelSavingDraft } = useDraftDataSetter({
        setters: {
            setRequiredDataChanged,
            setIsSavingDraft,
            setCampaignDraftData,
            setSavingDraftStatus
        } as Record<string, Dispatch<unknown>>,
        campaignDraftData,
        requiredDataChanged,
        releaseTitle: customize.releaseTitle,
        retrievingDraftData,
        dataToSaveDraft: { customize, details, selectedTab: selectedPreSaveTab.id as PresaveStep },
        draft,
        currentBrand,
        savingDraftStatus
    })

    const renderMiddlePreSaveContent = () => {
        return (
            <>
                {PresaveStep.CUSTOMIZE === selectedPreSaveTab.id &&
                    <TabSymphony
                        className="tabPanelWithPreSaveStuff"
                        visible
                    >
                        <CustomizeTab
                            draft={draft!}
                            website={website!}
                            brandArtwork={brandArtwork!}
                            contentName={contentName!}
                            selectedTab={selectedPreSaveTab.id as PresaveStep}
                        />
                    </TabSymphony>
                }
                {PresaveStep.DETAILS === selectedPreSaveTab.id &&
                    <TabSymphony
                        className="tabPanelWithPreSaveStuff"
                        visible
                    >
                        <DetailsTab
                            className="tabPanelWithPreSaveStuff"
                            website={website!}
                            brandArtwork={brandArtwork!}
                        />
                    </TabSymphony>
                }

                {PresaveStep.REVIEW === selectedPreSaveTab.id &&
                    <TabSymphony
                        className="tabPanelWithPreSaveStuff"
                        visible
                    >
                        <ReviewTab
                            website={website!}
                            brandArtwork={brandArtwork!}
                            contentName={contentName!}
                            selectedTab={selectedPreSaveTab.id as PresaveStep}
                        />
                    </TabSymphony>
                }
            </>
        )
    }

    const renderRightSideRecordContent = () => {
        return (
            <div
                className={clsx('customizeTabDataRightColumn')}
                style={sty.customizeTabDataRightColumn}
            >
                {/* <div className={containerClassName} style={containerStyle}> */}
                <div>
                    {isEqual(selectedBackgroundType, BackgroundTypes.ARTWORK) && (
                        <img
                            alt="artwork_as_background"
                            src={customize.artwork || brandArtwork || ''}
                            className="filter blur-lg absolute w-auto object-cover h-full"
                        />
                    )}
                    <RecordPreview
                        className={classes.principalPreviewContainer}
                        contentDetails={{
                            name: customize.releaseTitle,
                            options: musicPlatforms ?
                                musicPlatforms.filter((o) => o.show)
                                :
                                [
                                    { name: "spotify", show: true },
                                    { name: "apple_music", show: true },
                                    { name: "audiomack", show: true },
                                    { name: "soundcloud", show: true },
                                ],
                            artworkUrl: customize.artwork || brandArtwork || '',
                            subtitle: subtitle ?? '',
                        }}
                        titleAlignment={titleAlignment}
                        backgroundType={selectedBackgroundType}
                        backgroundColor={backgroundColor}
                        mainColor={primaryColor}
                        secondaryColor={
                            styleType === STACKED
                                ? primaryColor
                                : buttonColor
                        }
                        styleType={styleType}
                    />
                </div>
            </div>
        )
    }

    if (showPostCreation) {
        return (
            <Grid>
                <TopBar
                    className='preSaveTopBar2'
                    title={mainTitle}
                />
                <MainContentContainer className="h-full w-full">
                    <PostCreation
                        draft={campaignDraftData?.id as string}
                        brandId={brandId}
                        setGoal={setGoal}
                        setSelectedTab={(tab: TabItem) => setSelectedPreSaveTab(tab)}
                    />
                </MainContentContainer>
            </Grid>
        )
    }

    // NOTE: render()
    return (
        <>
            <div
                className="preSaveComponent"
                style={sty.preSaveComponent}
            >
                <TopBar
                    className='preSaveTopBar1'
                    title={mainTitle}
                    showSaveDraftChip={!!draft || !!customize.releaseTitle}
                    isCloseIcon
                    savingChanges={isSavingDraft}
                    handleClose={handleClickCloseTopBar}
                />
                <TabsSymphony
                    className="tabsPreSave"
                    tabs={PRESAVE_TABS}
                    selectedTab={selectedPreSaveTab}
                    type="numbers"
                    style={{ maxWidth: 540, marginLeft: 'auto', marginRight: 'auto' }}
                    isHeader
                />
                <PageSymphony
                    className="preSaveContentPage"
                    isStepper
                    isContent
                >
                    {showLoading &&
                        <Loader
                            className="loadingPreSaveLoader"
                            title="Loading Pre-Save..."
                            size={44}
                            isCentered
                        />
                    }
                    {!showLoading &&
                        <div
                            className="preSaveContentInnerContainer"
                            style={sty.preSaveContentInnerContainer}
                        >
                            {renderMiddlePreSaveContent()}
                            {!mobileView && renderRightSideRecordContent()}
                        </div>
                    }
                </PageSymphony>
                <PrevNextFooter
                    className="preSavePrevNextFooter"
                    tabs={PRESAVE_TABS}
                    currentTab={selectedPreSaveTab}
                    handlePrevTab={handleClickBackButton}
                    handleNextTab={handleClickNextButton}
                    isAboveContainer
                    isNextButtonDisabled={isNextButtonDisabled()}
                    aboveContainerChildren={
                        <div>
                            <ButtonSymphony
                                className="previewPreSaveButton"
                                onClick={() => setShowPreview(true)}
                                variant="text"
                            >
                                Preview Pre-Save
                            </ButtonSymphony>
                        </div>
                    }
                />
            </div>

            {/* MODALS */}
            <ModalSymphony
                className="previewPresaveWebsiteModal"
                isOpen={showPreview}
                onClose={() => setShowPreview(false)}
                style={sty.previewPresaveWebsiteModal}
            >
                {mobileView && renderRightSideRecordContent()}
                <ButtonSymphony
                    className="closePreviewButton"
                    onClick={() => setShowPreview(false)}
                    variant="text"
                >
                    Close Preview
                </ButtonSymphony>
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    preSaveComponent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    customizeTabDataRightColumn: {
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        overflow: 'hidden',
        borderRadius: 8,
        maxWidth: 400,
    },
    preSaveContentInnerContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
    },
    previewPresaveWebsiteModal: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        paddingTop: 16
    }
}

export default PreSave
